/**
 * Versions of an artifact (plan 7.1, 7.5): the version list, one version's
 * detail (documents carry their `ArtifactDocument`, pages their findings),
 * restore, and one place that refreshes everything an edit changes (the
 * artifact, its versions, its preview links, its head checks and its site).
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { ArtifactVersionRestoreResult } from '@tabbio/types';
import {
  getArtifactVersion,
  listArtifactVersions,
  restoreArtifactVersion,
} from '../lib/artifact-edits-api';
import { QUERY_KEYS } from '../lib/query-keys';
import { useAgentCapabilities } from './useAgentCapabilities';

/** The newest 50 versions, newest first. */
export function useArtifactVersions(artifactId: string | null | undefined, options: { enabled?: boolean } = {}) {
  const enabled = Boolean(artifactId) && (options.enabled ?? true);
  return useQuery({
    queryKey: QUERY_KEYS.ARTIFACT_VERSIONS(artifactId ?? ''),
    queryFn: () => listArtifactVersions(artifactId as string, { limit: 50 }),
    enabled,
    staleTime: 15_000,
  });
}

/** One version's detail: its document, variants, stale formats and findings. */
export function useArtifactVersion(
  artifactId: string | null | undefined,
  versionId: string | null | undefined,
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  const enabled = Boolean(artifactId && versionId) && (options.enabled ?? true);
  return useQuery({
    queryKey: QUERY_KEYS.ARTIFACT_VERSION(artifactId ?? '', versionId ?? ''),
    queryFn: () => getArtifactVersion(artifactId as string, versionId as string),
    enabled,
    staleTime: 10_000,
    refetchInterval: options.refetchInterval ?? false,
  });
}

/**
 * Everything a new version changes: the detail (the head, so every card and
 * the panel read it again and the preview link is minted anew), the
 * versions, the head's checks and the site.
 */
export function invalidateArtifactQueries(queryClient: QueryClient, artifactId: string) {
  void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ARTIFACT(artifactId) });
  void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ARTIFACT_VERSIONS(artifactId) });
  void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ARTIFACT_HEAD_CHECKS(artifactId) });
  void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ARTIFACT_SITE(artifactId) });
}

/** Refreshes everything a saved edit, a restore or an image edit changes. */
export function useInvalidateArtifact() {
  const queryClient = useQueryClient();
  return useCallback((artifactId: string) => invalidateArtifactQueries(queryClient, artifactId), [queryClient]);
}

/** Restore: the chosen version's content becomes a new head version. */
export function useRestoreArtifactVersion(artifactId: string | null | undefined) {
  const invalidate = useInvalidateArtifact();
  return useMutation<ArtifactVersionRestoreResult, Error, string>({
    mutationFn: (versionId: string) => restoreArtifactVersion(artifactId as string, versionId),
    onSuccess: () => {
      if (artifactId) invalidate(artifactId);
    },
  });
}

/** Versions, manual edits and agent edits are on for this account (`capabilities.editing`). */
export function useEditingAvailable(): boolean {
  const { capabilities } = useAgentCapabilities();
  return capabilities?.editing?.available === true;
}
